import { Grid } from '@mui/material';
import { useMaterialUIController } from 'context';
import PropTypes from 'prop-types';
import { useState } from 'react';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useFileViewer } from 'hook/useFileViewer';
import FilesItem from 'components/Lists/FilesItem';
import Badge from '@mui/material/Badge';
import MDTypography from 'components/MDTypography';

/**
 * FilesList displays a list of files, optionally allowing for sorting and grouping by type.
 * Each file can be represented by an icon or a label (chips), and the component supports selecting
 * a file to display it. Files are grouped and sorted based on the provided props, and
 * a menu can be opened to show multiple files of the same type for selection.
 *
 * Props:
 * @param {Object[]} files - An array of file objects to display. Each file object must have
 *                           contentId, hash, and can optionally include label, type, entityId,
 *                           shortLabel, tooltip, and color properties.
 * @param {boolean} [iconLetterDisplayed=true] - Flag indicating whether to display an icon or
 *                                               a label for each file. If true, icons are displayed.
 * @param {boolean} [sortEnabled=true] - Flag indicating whether the files should be sorted. If
 *                                       true, files are sorted based on their type and label.
 * @param {string} selectedId - The ID of the currently selected file, if any.
 * @param {string} viewName - The name of the view mode used when opening files, which can
 *                            influence how files are displayed or interacted with.
 * @param {function} onFileClick - The file click handle function
 */
export default function FilesList({
  files,
  iconLetterDisplayed,
  sortEnabled,
  selectedId,
  viewName,
  onFileClick
}) {
  const {
    controller: { primaryColor }
  } = useMaterialUIController();
  const { displayFile: openFile } = useFileViewer(viewName);
  const [anchorEl, setAnchorEl] = useState(null);
  const [currentFiles, setCurrentFiles] = useState([]);

  const handleIconClick = (event, files) => {
    if (files?.length === 1) {
      handleOnFileItemClick(files[0]);
      return;
    }
    setAnchorEl(event.currentTarget);
    setCurrentFiles(files);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const sortedFiles = files ? [...files] : [];
  if (sortedFiles && sortEnabled) {
    sortedFiles.sort(filesSort);
  }
  const groupedFiles = groupFilesByType(sortedFiles, iconLetterDisplayed);

  return (
    <Grid container spacing={1}>
      {Object.entries(groupedFiles).map(([type, files]) => (
        <Grid item key={type}>
          <FilesItem
            files={files}
            iconLetterDisplayed={iconLetterDisplayed}
            selectedId={selectedId}
            handleIconClick={handleIconClick}
            handleOnFileItemClick={handleOnFileItemClick}
          />
        </Grid>
      ))}
      <Menu id="file-menu" anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
        {currentFiles.map((file, index) => (
          <MenuItem
            key={index}
            onClick={() => {
              openFile(file);
              handleClose();
            }}
            sx={
              selectedId && selectedId === file.contentId
                ? {
                    color: primaryColor,
                    fontWeight: 'bold'
                  }
                : {}
            }
          >
            <Badge
              anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
              variant="dot"
              color="warning"
              invisible={file.status === 'ACTIVE'}
            >
              <MDTypography variant="body2">{fileTitle(file)}</MDTypography>
            </Badge>
          </MenuItem>
        ))}
      </Menu>
    </Grid>
  );

  function handleOnFileItemClick(file) {
    openFile(file);
    onFileClick(file);
  }

  function fileTitle(file) {
    return file.tooltip !== file.label ? file.tooltip ?? file.label : '';
  }

  function groupFilesByType(files, iconLetterDisplayed) {
    return files.reduce((acc, file) => {
      const { type, entityId } = file;
      const key = iconLetterDisplayed ? type : entityId;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(file);
      return acc;
    }, {});
  }

  function filesSort(a, b) {
    function localCompare(a, b) {
      return fileTitle(a).localeCompare(fileTitle(b));
    }

    if (a === b) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    if (a.type === FileTypes.Report) {
      if (b.type !== FileTypes.Report) return -1;
      return localCompare(a, b);
    }
    if (a.type === FileTypes.Delib) {
      if (b.type === FileTypes.Report) return 1;
      if (b.type !== FileTypes.Delib) return -1;
      return localCompare(a, b);
    }
    if (a.type === FileTypes.ReportAnnexe) {
      if (b.type === FileTypes.Report) return 1;
      if (b.type === FileTypes.Delib) return 1;
      if (b.type !== FileTypes.ReportAnnexe) return -1;
      return localCompare(a, b);
    }
    return 0;
  }
}

export const FileTypes = {
  SeanceAnnexe: 'SEANCE_ANNEXE',
  Report: 'REPORT_FILE',
  ReportAnnexe: 'REPORT_ANNEXE',
  Delib: 'DELIB'
};

export const getIconLetter = (type) => {
  if (type === FileTypes.Report) {
    return 'R';
  }
  if (type === FileTypes.Delib) {
    return 'D';
  }
  return 'A';
};

FilesList.propTypes = {
  files: PropTypes.arrayOf(
    PropTypes.shape({
      contentId: PropTypes.string.isRequired,
      hash: PropTypes.string,
      mimeType: PropTypes.string,
      label: PropTypes.string,
      shortLabel: PropTypes.string,
      tooltip: PropTypes.string,
      type: PropTypes.string,
      entityId: PropTypes.number,
      color: PropTypes.string,
      status: PropTypes.string
    })
  ).isRequired,
  iconLetterDisplayed: PropTypes.bool,
  sortEnabled: PropTypes.bool,
  selectedId: PropTypes.string,
  viewName: PropTypes.string,
  onFileClick: PropTypes.func
};

FilesList.defaultProps = {
  iconLetterDisplayed: true,
  sortEnabled: true,
  onFileClick: () => {}
};
